// GlobalSearch.tsx
import {
  FunctionComponent,
  KeyboardEvent,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router';
import './GlobalSearch.css';

import { useStoreActions, useStoreState } from '../../store';
import { useAuth } from '../../utility/useAuth';

import {
  getJobDetails,
  getOperationFilters,
  getUserPreference,
  getWarehouses,
  setUserPreference,
  getJobSummaries,
  getJobPlanningSummaries
} from '../../services/ApiService';

import { DATA_ERRORS, SETTINGS_ERRORS } from '../../utility/errorCatalog';
import { reportError, serverMessageOf, statusOf } from '../../utility/reportError';

import SEARCH_TEXT_SRC from '../../Icons/Search.svg';
import MINUS_SRC from '../../Icons/minus.svg';
import PLUS_SRC from '../../Icons/plus.svg';
import BARCODE_SRC from '../../Icons/Barcode.svg';

import { OperationFilter } from '../../types/OperationFilter';
import { UserPreference } from '../../types/UserPreference';
import { parseSearchValue } from '../../utility/jobFilters';
import {
  resetSmartSearchRetirement,
  setSmartSearchSlot,
  useSmartSearchUsable,
} from '../../utility/searchMode';

/* Lazy: the scanner pulls in a wasm barcode-detector polyfill, and this row is
   part of the always-loaded layout — only fetch it when someone taps scan. */
const BarcodeSearchModal = lazy(
  () =>
    import(
      /* webpackChunkName: "barcode-search" */ '../BarcodeSearchModal/BarcodeSearchModal'
    )
);

/** The scan button only shows where a camera can actually be asked for:
 *  `mediaDevices` is absent on plain http and in older browsers. */
export const canUseCamera = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getUserMedia === 'function';

// ⬇️ lifted state props
interface GlobalSearchProps {
  filtersOpen: boolean;
  onToggleFilters: (open: boolean) => void;
}

// local extension
type UserPreferenceExt = UserPreference & {
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
};




const GlobalSearch: FunctionComponent<GlobalSearchProps> = ({
  filtersOpen,
  onToggleFilters
}) => {
  const navigate = useNavigate();
  const auth = useAuth();

  

  const {
    setSearchValue,
    setWarehouses: setSelectedWarehouses,
    setOperationFilters,
    setBpNames,
    setJobs,
  } = useStoreActions((a) => a.appSettings);

  const { jobs, warehouses: selectedWarehouses, operationFilters, searchValue } =
    useStoreState((s) => s.appSettings);

    const clearAllFilters = () => {
  setSelectedWarehouses([]);
  setOperationFilters([]);
  setBpNames([]);
  setSearchValue('');

};


  const [search, setSearch] = useState('');
  const [warehouses, setWarehouses] = useState<string[]>([]);
  const [filters, setFilters] = useState<OperationFilter[]>([]);
  const [userPref, setUserPref] = useState<UserPreferenceExt | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [cameraAvailable] = useState(canUseCamera);

  /* ---------- one context-sensitive field ----------
     Where CoSMo can run, the field is `FilterAssist`, rendered by `GlobalFilter`
     (it owns the filters it sets) and portalled into the slot below; it tries a
     plain search first and asks CoSMo only when nothing matches. Elsewhere it is
     this component's plain box. See utility/searchMode.ts.                  */
  const showSmart = useSmartSearchUsable();

  useEffect(() => {
    resetSmartSearchRetirement();
  }, []);

  /* ---------- the box is DERIVED from the store ----------
     This row unmounts whenever you leave /home, so a box holding its own copy
     of the search term came back empty over a board that was still filtering
     by it — the control said "no search", the cards said otherwise. The term
     itself lives in `searchValue` (alongside the due-date tokens, which
     `parseSearchValue` separates out), and that is persisted, so reading it
     back here is what makes a search survive a trip to another screen.

     It only re-reads when the STORE changes, so it never fights the operator
     mid-type: `handleChange` writes local state and nothing else until Enter
     or the search button commits it.                                        */
  useEffect(() => {
    setSearch(parseSearchValue(searchValue).text ?? '');
  }, [searchValue]);

  /* The saved preference below seeds the resource filter, and it resolves
     asynchronously on EVERY mount of this row. Read the live selection at that
     moment rather than the one captured when the request went out. */
  const operationFiltersRef = useRef(operationFilters);
  operationFiltersRef.current = operationFilters;

  // ---------- Load initial data ----------
  useEffect(() => {
    getWarehouses(auth)
      .then(setWarehouses)
      .catch((error) =>
        reportError(DATA_ERRORS.WAREHOUSES, error, {
          serverMessage: serverMessageOf(error),
          status: statusOf(error),
        })
      );

    getOperationFilters(auth)
      .then((data) => setFilters([{} as OperationFilter, ...data]))
      .catch((error) =>
        reportError(DATA_ERRORS.RESOURCE_FILTERS, error, {
          serverMessage: serverMessageOf(error),
          status: statusOf(error),
        })
      );

    getUserPreference(auth)
      .then((data) => {
        const pref = (data ?? {}) as UserPreferenceExt;
        setUserPref(pref);

        // The saved preference is a single filter; it SEEDS the (now
        // multi-select) list with one chip — and seeding is a first-visit
        // decision, not a per-mount one. This row remounts every time you come
        // back to /home, so applying it unconditionally replaced whatever the
        // operator had chipped (or re-added the chip they had just removed)
        // with the one saved value, on every return to the board.
        if (pref.operationFilter && operationFiltersRef.current.length === 0) {
          setOperationFilters([pref.operationFilter]);
        }
      })
      .catch((error) =>
        reportError(SETTINGS_ERRORS.PREFERENCE_LOAD, error, {
          serverMessage: serverMessageOf(error),
          status: statusOf(error),
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Search handlers ----------
  const searchClick = () => {
    setSearchValue(search);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchClick();
    }
  };

  // A scan is a search: fill the box and commit it, as if typed + Enter.
  const handleScan = useCallback(
    (value: string) => {
      setScannerOpen(false);
      // Either box shows the term: the CoSMo box reads the active search text
      // back from the store, exactly as the plain box does.
      setSearch(value);
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const closeScanner = useCallback(() => setScannerOpen(false), []);

  const handleChange = (value: string) => {
    setSearch(value);
    if (value === '') {
      setSearchValue('');
    }
  };

  // ---------- Render ----------
  return (
    <div
      className={`global-search-row ${
        !filtersOpen ? 'global-search-row--collapsed' : ''
      }`}
    >
      {/* ✅ Search input (hidden when toggled closed) */}
      {filtersOpen && (
        <div className="global-search-group">
        {showSmart ? (
          <div className="global-search-smart-slot" ref={setSmartSearchSlot} />
        ) : (
        <div className="global-search">
          <button
            type="button"
            className="left-icon-btn"
            onClick={searchClick}
            aria-label="Search"
            title="Search"
          >
            <img
              src={SEARCH_TEXT_SRC}
              alt=""
              aria-hidden="true"
              className="icon"
            />
          </button>

          <input
            className="search-input"
            data-csm-step="search.input"
            type="text"
            value={search}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search all production orders"
            aria-label="Search all production orders"
          />
        </div>
        )}


          {cameraAvailable && (
            <button
              type="button"
              className="barcode-scan-btn"
              data-csm-step="search.scan"
              onClick={() => setScannerOpen(true)}
              aria-label="Scan a barcode to search"
              title="Scan a barcode"
            >
              <img
                src={BARCODE_SRC}
                alt=""
                aria-hidden="true"
                className="icon"
              />
            </button>
          )}
        </div>
      )}

      {scannerOpen && (
        <Suspense fallback={null}>
          <BarcodeSearchModal
            open={scannerOpen}
            onScan={handleScan}
            onClose={closeScanner}
          />
        </Suspense>
      )}

      {/* ✅ Right‑aligned toggle */}
      <div
        className="right-toggle"
        data-csm-step="search.toggle"
        role="button"
        tabIndex={0}
        aria-expanded={filtersOpen}
        onClick={() => {
  if (filtersOpen) {
    clearAllFilters(); // ✅ clear only when closing
  }
  onToggleFilters(!filtersOpen);
}}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key === 'Enter' || e.key === ' ') {
    if (filtersOpen) {
      clearAllFilters();
    }
    onToggleFilters(!filtersOpen);
  }
}}
      >
        <div className="toggle-icon">
          <img
            src={filtersOpen ? MINUS_SRC : PLUS_SRC}
            alt=""
            aria-hidden="true"
            className="toggle-svg"
          />
        </div>

        <div className="toggle-text">
          {filtersOpen ? 'Hide search/filter' : 'Open search/filter'}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;